<template>
    <div class="stats-filters">
        <div class="filters-header">
            <h3>Фильтры</h3>
            <button type="button" class="toggle-btn" @click="isExpanded = !isExpanded">
                {{ isExpanded ? 'Свернуть' : 'Развернуть' }}
            </button>
        </div>

        <div v-show="isExpanded" class="filters-content">
            <div class="filters-toolbar">
                <UiSelect
                    v-model="localFilters.season_ids"
                    label="Сезон"
                    :options="seasonOptions"
                    multiple
                    show-select-all
                    value-type="number"
                    align="left"
                    placeholder="Выберите сезон"
                    :min-selected="1"
                    @change="onSeasonChange"
                />

                <UiSelect
                    v-model="localFilters.competition_types"
                    label="Вид игры"
                    :options="competitionTypes"
                    multiple
                    show-select-all
                    value-type="string"
                    align="left"
                    placeholder="Все виды"
                    @change="onCompetitionChange"
                />

                <UiSelect
                    v-if="localFilters.competition_types.length > 0"
                    v-model="localFilters.stages"
                    label="Этап"
                    :options="availableStages"
                    multiple
                    show-select-all
                    value-type="string"
                    align="left"
                    wide
                    placeholder="Все этапы"
                    @change="onStageChange"
                />

                <UiSelect
                    v-model="localFilters.match_ids"
                    label="Матчи"
                    :options="matchOptions"
                    multiple
                    show-select-all
                    value-type="number"
                    align="left"
                    wide
                    placeholder="Все матчи"
                    :summary-fn="matchesSummary"
                />

                <div class="filters-actions">
                    <button type="button" class="btn-primary" @click="applyFilters">Применить</button>
                    <button type="button" class="btn-secondary" @click="resetFilters">Сбросить</button>
                    <button type="button" class="btn-export" @click="exportStats">Экспорт в Excel</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import UiSelect from '@/components/ui/forms/UiSelect.vue';

const props = defineProps({
    seasons: {
        type: Array,
        default: () => [],
    },
    matches: {
        type: Array,
        default: () => [],
    },
    filterData: {
        type: Object,
        default: () => ({
            actionSubtypes: [],
        }),
    },
    initialFilters: {
        type: Object,
        default: () => ({}),
    },
    defaultExpanded: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['filter-change', 'export']);

const isExpanded = ref(props.defaultExpanded);

const localFilters = ref({
    season_ids: props.initialFilters.season_ids || [],
    competition_types: props.initialFilters.competition_types || [],
    stages: props.initialFilters.stages || [],
    match_ids: props.initialFilters.match_ids || [],
    action_subtypes: [],
});

const competitionTypes = [
    { value: 'championship', label: 'Чемпионат' },
    { value: 'cup', label: 'Кубок' },
    { value: 'supercup', label: 'Суперкубок' },
];

const stageOptions = {
    championship: [
        { value: 'championship_preliminary', label: 'Предварительный (2 круга)' },
        { value: 'quarterfinal', label: 'Четвертьфинальный' },
        { value: 'semifinal', label: 'Полуфинальный' },
        { value: 'final_1_4', label: 'Финальный за 1-4 места' },
        { value: 'final_5_9', label: 'Финальный за 5-9 места (2 круга)' },
    ],
    cup: [
        { value: 'group_stage', label: 'Групповой (3 группы по 3 команды, 2 круга)' },
        { value: 'cup_additional', label: 'Дополнительный (3 игры)' },
        { value: 'final', label: 'Финальный' },
    ],
    supercup: [],
};

const seasonOptions = computed(() =>
    props.seasons.map((season) => ({
        value: season.id,
        label: season.name,
    }))
);

const availableStages = computed(() => {
    const stages = [];
    localFilters.value.competition_types.forEach((compType) => {
        if (stageOptions[compType]) {
            stages.push(...stageOptions[compType]);
        }
    });
    return stages;
});

const filteredMatches = computed(() => {
    let matches = props.matches;

    if (localFilters.value.season_ids.length > 0) {
        matches = matches.filter((m) => localFilters.value.season_ids.includes(m.season_id));
    }

    if (localFilters.value.competition_types.length > 0) {
        matches = matches.filter(
            (m) => m.competition_type && localFilters.value.competition_types.includes(m.competition_type)
        );
    }

    if (localFilters.value.stages.length > 0) {
        matches = matches.filter((m) => {
            return localFilters.value.stages.some((stage) => {
                const matchStage = m.stage;
                if (stage === 'championship_preliminary') {
                    return matchStage === 'regular-1' || matchStage === 'regular-2';
                }
                if (stage === 'final_1_4') {
                    return matchStage === 'final';
                }
                if (stage === 'final_5_9') {
                    return matchStage === '5-places';
                }
                if (stage === 'cup_additional') {
                    return matchStage === 'dop';
                }
                return matchStage === stage;
            });
        });
    }

    return matches;
});

const matchOptions = computed(() =>
    filteredMatches.value.map((match) => ({
        value: match.id,
        label: `${formatMatchDate(match.date)} ${formatMatchTeams(match)}`,
    }))
);

function matchesSummary(ids) {
    if (!ids.length) {
        return 'Все матчи';
    }
    return `${ids.length} из ${filteredMatches.value.length}`;
}

function pruneMatchIds() {
    const filteredIds = filteredMatches.value.map((m) => m.id);
    localFilters.value.match_ids = localFilters.value.match_ids.filter((id) => filteredIds.includes(id));
}

function onSeasonChange() {
    pruneMatchIds();
}

function onCompetitionChange() {
    const availableStageValues = availableStages.value.map((s) => s.value);
    localFilters.value.stages = localFilters.value.stages.filter((stage) =>
        availableStageValues.includes(stage)
    );
    pruneMatchIds();
}

function onStageChange() {
    pruneMatchIds();
}

function applyFilters() {
    emit('filter-change', { ...localFilters.value });
}

function resetFilters() {
    const currentSeason = props.seasons.find((s) => s.is_active) || props.seasons[0];
    const currentSeasonMatches = props.matches
        .filter((m) => m.season_id === currentSeason?.id)
        .map((m) => m.id);

    localFilters.value = {
        season_ids: currentSeason ? [currentSeason.id] : [],
        competition_types: ['championship', 'cup', 'supercup'],
        stages: [],
        match_ids: currentSeasonMatches,
        action_subtypes: [],
    };

    applyFilters();
}

function exportStats() {
    emit('export', { ...localFilters.value });
}

function formatMatchDate(dateString) {
    if (!dateString) return '';

    try {
        if (dateString.includes('-')) {
            const parts = dateString.split('-');
            if (parts.length >= 3) {
                return `${parts[2].padStart(2, '0')}.${parts[1].padStart(2, '0')}.${parts[0]}`;
            }
            return dateString;
        }

        if (dateString.includes('.')) {
            const parts = dateString.split('.');
            if (parts.length === 3) {
                return `${parts[0].padStart(2, '0')}.${parts[1].padStart(2, '0')}.${parts[2]}`;
            }
            return dateString;
        }

        return dateString;
    } catch (e) {
        return dateString;
    }
}

function formatMatchTeams(match) {
    if (!match) return '';
    if (match.home_team && match.away_team) {
        return `${match.home_team} — ${match.away_team}`;
    }
    if (match.home_team_short && match.away_team_short) {
        return `${match.home_team_short} — ${match.away_team_short}`;
    }
    return match.label || '';
}
</script>

<style lang="scss" scoped>
@import '../../../scss/tokens';

.stats-filters {
    background: #fff;
    border-radius: 10px;
    padding: 1rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    border: 1px solid #f1f5f9;
}

.filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
        font-size: 1rem;
        font-weight: 600;
        color: #1e293b;
        margin: 0;
    }
}

.toggle-btn {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #e2e8f0;
    }
}

.filters-content {
    margin-top: 1rem;
}

.filters-toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;

    @include up($bp-md) {
        flex-direction: row;
        flex-wrap: wrap;
        align-items: flex-start;
    }
}

.filters-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
}

.btn-primary {
    padding: 0.5rem 1rem;
    background: #275092;
    color: #fff;
    border: none;
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        opacity: 0.92;
    }
}

.btn-secondary {
    padding: 0.5rem 1rem;
    background: #fff;
    color: #275092;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        border-color: #275092;
    }
}

.btn-export {
    padding: 0.5rem 1rem;
    background: #fff;
    color: #475569;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        border-color: #94a3b8;
    }
}

</style>
